/**
 * Auth Context
 *
 * Provides authentication state and methods throughout the app.
 * Handles JWT token storage, automatic refresh, and session validation.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { UserResponse } from '@functieloonhuis/shared';
import type { Role } from '@functieloonhuis/shared';
export type { Role };
import { authApi, hasTokens, clearTokens, ApiRequestError, setAuthFailureCallback, clearAuthFailureCallback } from '../../lib/api';

interface AuthContextType {
  user: UserResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (roles: Role | Role[]) => boolean;
  /**
   * Epic 5 (Verlof MVP, Story 5.1/5.2) — the full "leidinggevende" capability
   * (AD-4's `requireTeamManagerOrHR` predicate): true when the user is
   * `hr`/`admin` OR the server-computed `user.isTeamManager` is true
   * (`managementService.isTeamManager`, AD-3). "Leidinggevende" is a derived
   * capability, never a `hasRole('leidinggevende')` enum check (AD-4
   * explicitly forbids that). Single source reused by both the dashboard
   * (Story 5.1) and the navigation (Story 5.2) — do not re-derive this
   * combination ad-hoc elsewhere.
   */
  isTeamManagerOrHR: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Check if user has one of the specified roles
   */
  const hasRole = useCallback((roles: Role | Role[]): boolean => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role as Role);
  }, [user]);

  /**
   * AD-4's full `requireTeamManagerOrHR` predicate, mirrored client-side for
   * UI gating (Story 5.1 dashboard, Story 5.2 navigation): hr/admin always
   * qualify server-side for the approval-queue/attendance-calendar
   * endpoints this gates, exactly like a team-manager does — see
   * `leaveRequestService.getApprovalQueue` / `calendarService.resolveScope`.
   */
  const isTeamManagerOrHR = !!user && (hasRole(['hr', 'admin']) || user.isTeamManager === true);

  /**
   * Verify current session on mount
   */
  useEffect(() => {
    async function checkAuth() {
      // Skip if no tokens stored
      if (!hasTokens()) {
        setIsLoading(false);
        return;
      }

      try {
        const userData = await authApi.me();
        setUser(userData);
      } catch (error) {
        // Session invalid, clear tokens
        // Check for both UNAUTHORIZED and INVALID_TOKEN codes, or 401 status
        if (error instanceof ApiRequestError &&
            (error.code === 'UNAUTHORIZED' || error.code === 'INVALID_TOKEN' || error.status === 401)) {
          clearTokens();
        }
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    checkAuth();
  }, []);

  /**
   * Set up auth failure callback to handle 401s from any API call
   */
  useEffect(() => {
    // Register callback that will be called when API requests fail with 401 after refresh
    setAuthFailureCallback(() => {
      clearTokens();
      setUser(null);
    });

    return () => clearAuthFailureCallback();
  }, []);

  /**
   * Set up token refresh interval
   */
  useEffect(() => {
    if (!user) return;

    // Refresh token every 10 minutes (access token expires in 15 min)
    const interval = setInterval(async () => {
      const success = await authApi.refresh();
      if (!success) {
        clearTokens();
        setUser(null);
        return;
      }
      // Het nieuwe token kan een andere rol dragen (rollenscherm, of de
      // schakelaar AUTH_ROLE_APP_SCOPE is omgegaan). Zonder deze stap zou de
      // navigatie tot een herlaad de oude rol tonen terwijl de verzoeken de
      // nieuwe dragen.
      try {
        setUser(await authApi.me());
      } catch {
        // Een mislukte /me laat de bestaande gebruiker staan; de volgende
        // vernieuwing probeert het opnieuw.
      }
    }, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  /**
   * Login with email and password
   */
  async function login(email: string, password: string): Promise<void> {
    const response = await authApi.login(email, password);
    setUser(response.user);
  }

  /**
   * Logout and clear session
   */
  async function logout(): Promise<void> {
    await authApi.logout();
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        hasRole,
        isTeamManagerOrHR,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
